import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Box,
  Input,
  Button,
  Typography,
  Container,
  Alert,
  Card,
  FormControl,
  FormLabel,
  FormHelperText,
} from '@mui/joy';
import { useAuth } from '@/contexts/AuthContext';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import type { AxiosError } from 'axios';
import { useEffect } from 'react';
import isologo from '@/assets/isologo.png';
import { pageTitle } from '@/utils/pageTitle';

const loginSchema = z.object({
  email: z.string().email('Por favor ingrese un email válido'),
  password: z.string().min(6, 'La contraseña debe tener al menos 6 caracteres'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export const Route = createFileRoute('/_public/login')({
  component: LoginPage,
  head: () => pageTitle('Iniciar sesión'),
});

function LoginPage() {
  const navigate = useNavigate();
  const { loginUser, loginMutation, isAuthenticated, isSuperAdmin } = useAuth();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // Redirect to dashboard if login is successful
  useEffect(() => {
    if (isAuthenticated) {
      navigate({ to: isSuperAdmin ? '/tenants' : '/dashboard' });
    }
  }, [isAuthenticated, navigate, isSuperAdmin]);

  const onSubmit = async (data: LoginFormData) => {
    loginUser(data);
  };

  return (
    <Container maxWidth='xs'>
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: 'calc(100vh - 64px)',
          justifyContent: 'center',
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 400, p: 4, boxShadow: 'lg' }}>
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Box
              component='img'
              src={isologo}
              alt='AW'
              sx={{
                display: 'block',
                width: '100%',
                maxWidth: 160,
                height: 'auto',
                mx: 'auto',
                mb: 2,
                objectFit: 'contain',
              }}
            />
            <Typography level='h2' fontWeight={500}>
              ¡Bienvenido!
            </Typography>

            <Typography level='body-md'>
              Inicie sesión para continuar
            </Typography>
          </Box>

          <Box
            component='form'
            onSubmit={handleSubmit(onSubmit)}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <Controller
              name='email'
              control={control}
              render={({ field }) => (
                <FormControl error={!!errors.email}>
                  <FormLabel>Correo electrónico</FormLabel>
                  <Input
                    {...field}
                    type='email'
                    placeholder='Ingrese su correo electrónico'
                    required
                    disabled={loginMutation.isPending}
                    autoComplete='email'
                    autoFocus
                  />
                  {errors.email && (
                    <FormHelperText>{errors.email.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name='password'
              control={control}
              render={({ field }) => (
                <FormControl error={!!errors.password}>
                  <FormLabel>Contraseña</FormLabel>
                  <Input
                    {...field}
                    type='password'
                    placeholder='••••••••'
                    required
                    disabled={loginMutation.isPending}
                    autoComplete='current-password'
                  />
                  {errors.password && (
                    <FormHelperText>{errors.password.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />

            {loginMutation.error && (
              <Alert color='danger' sx={{ mb: 3 }}>
                {(() => {
                  const error = loginMutation.error as AxiosError<{
                    errors?: Record<string, string>;
                  }>;
                  const errorResponse = error?.response?.data;

                  if (errorResponse?.errors) {
                    const errors = errorResponse.errors;
                    const errorMessage = errors.Error || errors[Object.keys(errors)[0]];
                    if (errorMessage) {
                      return errorMessage;
                    }
                  }

                  if (error?.response?.status === 401) {
                    return 'Credenciales incorrectas';
                  }

                  return 'Error al iniciar sesión. Por favor intente nuevamente.';
                })()}
              </Alert>
            )}

            <Button
              type='submit'
              fullWidth
              loading={loginMutation.isPending}
              variant='outlined'
              color='neutral'
              sx={{ mt: 1 }}
            >
              Iniciar sesión
            </Button>
          </Box>
        </Card>
      </Box>
    </Container>
  );
}
